import { useState, useCallback } from "react";
import { ShoppingBag, Search, Send, Loader2, CheckCircle, Package, ChevronDown, ChevronUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const ACCESSORY_TYPES = [
  { value: "capinha", label: "Capinha" },
  { value: "caixa_de_som", label: "Caixa de Som" },
  { value: "fone_de_ouvido", label: "Fone de Ouvido" },
  { value: "carregador", label: "Carregador" },
  { value: "pelicula", label: "Película" },
  { value: "smartwatch", label: "Smartwatch" },
  { value: "powerbank", label: "Power Bank" },
  { value: "suporte", label: "Suporte Veicular" },
  { value: "cabo", label: "Cabo/Adaptador" },
  { value: "outro", label: "Outro" },
];

interface AccessoryResult {
  name: string;
  type: string;
  description: string;
  features: string[];
}

const AccessorySuggestions = () => {
  const { toast } = useToast();
  const [productName, setProductName] = useState("");
  const [productType, setProductType] = useState("capinha");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [searchResult, setSearchResult] = useState<AccessoryResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const searchAccessory = useCallback(async () => {
    if (!productName.trim() || productName.trim().length < 3) return;
    setSearching(true);
    setSearchResult(null);
    setShowDetails(false);

    try {
      const apiKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const projectUrl = import.meta.env.VITE_SUPABASE_URL;

      const resp = await fetch(`${projectUrl}/functions/v1/search-accessory`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "apikey": apiKey,
          "Authorization": `Bearer ${apiKey}`,
        },
        body: JSON.stringify({ product_name: productName.trim(), product_type: productType }),
      });

      const data = await resp.json();
      if (data?.success && data?.data) {
        setSearchResult(data.data);
      }
    } catch {
      toast({ title: "Erro ao buscar produto", variant: "destructive" });
    } finally {
      setSearching(false);
    }
  }, [productName, productType, toast]);

  const handleSubmit = async () => {
    if (!productName.trim()) {
      toast({ title: "Digite o nome do produto", variant: "destructive" });
      return;
    }
    if (submitting) return;
    setSubmitting(true);

    const finalName = searchResult?.name || productName.trim();
    const typeLabel = ACCESSORY_TYPES.find(t => t.value === productType)?.label || productType;

    try {
      // Note: duplicate check removed — accessory_suggestions SELECT is now admin-only for security
      // The INSERT will still work via the public INSERT policy

      const { error } = await supabase.from("accessory_suggestions").insert({
        product_name: finalName,
        product_type: typeLabel,
        customer_name: customerName.trim() || null,
        customer_phone: customerPhone.trim() || null,
      });

      if (error) throw error;

      setSubmitted(true);
      toast({ title: "Sugestão enviada!", description: "Obrigado pela sua sugestão!" });
    } catch {
      toast({ title: "Erro ao enviar sugestão", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setProductName("");
    setProductType("capinha");
    setCustomerName("");
    setCustomerPhone("");
    setSearchResult(null);
    setSubmitted(false);
    setShowDetails(false);
  };

  if (submitted) {
    return (
      <section id="sugestoes-acessorios" className="py-20 bg-secondary/30">
        <div className="container mx-auto px-4 max-w-2xl">
          <div className="text-center py-12 space-y-4">
            <CheckCircle className="w-16 h-16 text-primary mx-auto" />
            <h3 className="font-display text-2xl font-bold text-foreground">Sugestão Enviada!</h3>
            <p className="text-muted-foreground">Vamos analisar e trazer novidades para você.</p>
            <button
              onClick={handleReset}
              className="mt-4 px-6 py-2 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 transition-all hover:scale-105"
            >
              Enviar outra sugestão
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="sugestoes-acessorios" className="py-20 bg-secondary/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-2 mb-4">
            <ShoppingBag className="w-8 h-8 text-primary" />
            <h2 className="font-display text-4xl md:text-5xl font-bold text-gradient-gold">Sugira um Acessório</h2>
          </div>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Qual acessório ou produto você gostaria de ver na nossa loja? Capinhas, caixas de som, fones e muito mais!
          </p>
        </div>

        <div className="max-w-lg mx-auto space-y-4">
          {/* Product type selector */}
          <div className="flex flex-wrap gap-2 justify-center">
            {ACCESSORY_TYPES.map((type) => (
              <button
                key={type.value}
                onClick={() => setProductType(type.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  productType === type.value
                    ? "bg-primary text-primary-foreground scale-105"
                    : "bg-card border border-border text-muted-foreground hover:text-foreground hover:border-primary/50"
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>

          {/* Product search */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Ex: JBL Flip 6, AirPods Pro, Capinha iPhone 16..."
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchAccessory()}
                className="w-full pl-10 pr-3 py-3 rounded-lg bg-card border border-border text-foreground placeholder:text-muted-foreground text-sm focus:border-primary focus:outline-none transition-colors"
                maxLength={100}
              />
            </div>
            <button
              onClick={searchAccessory}
              disabled={searching || productName.trim().length < 3}
              className="px-4 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Buscar acessório"
            >
              {searching ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
            </button>
          </div>

          {/* Search result */}
          {searchResult && (
            <div className="bg-card border border-border rounded-xl p-5 space-y-4">
              <div className="flex gap-4 items-start">
                <div className="w-16 h-16 flex-shrink-0 bg-secondary rounded-lg overflow-hidden flex items-center justify-center">
                  <Package className="w-8 h-8 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-display text-lg font-bold text-foreground">{searchResult.name}</h4>
                  <p className="text-sm text-primary font-semibold">{searchResult.type}</p>
                  <p className="text-sm text-muted-foreground mt-1">{searchResult.description}</p>
                </div>
              </div>

              {searchResult.features && searchResult.features.length > 0 && (
                <>
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="w-full flex items-center justify-center gap-1 text-sm text-primary font-semibold hover:opacity-80 transition-opacity py-1"
                  >
                    {showDetails ? "Ocultar detalhes" : "Ver características"}
                    {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>

                  {showDetails && (
                    <div className="bg-secondary/50 rounded-lg p-4 space-y-2">
                      <h5 className="text-sm font-bold text-foreground">Características</h5>
                      <ul className="space-y-1">
                        {searchResult.features.map((feat, i) => (
                          <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                            <span className="text-primary mt-0.5">•</span>
                            {feat}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Customer info */}
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Seu nome (opcional)"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-card border border-border text-foreground placeholder:text-muted-foreground text-sm focus:border-primary focus:outline-none transition-colors"
              maxLength={100}
            />
            <input
              type="tel"
              placeholder="Seu WhatsApp (opcional)"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-card border border-border text-foreground placeholder:text-muted-foreground text-sm focus:border-primary focus:outline-none transition-colors"
              maxLength={20}
            />
          </div>

          <button
            onClick={handleSubmit}
            disabled={submitting || !productName.trim()}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-gradient-gold text-primary-foreground font-semibold hover:opacity-90 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Send className="w-4 h-4" />
                Enviar Sugestão
              </>
            )}
          </button>
        </div>
      </div>
    </section>
  );
};

export default AccessorySuggestions;
